import React, { useContext, useEffect, useState } from "react";
import HotelRoomSearchBar from "../components/HotelRoomSearchBar";
import SingleUserSideRoomCard from "../components/SingleUserSideRoomCard";
import Footer from "../components/Footer";
import { useParams } from "react-router-dom";
import NothingToShow from "../components/NothingToShow";
import ErrorAlert from "../admin/components/ErrorAlert";
import { SearchContext } from "../context/HotelRoomSearch";

export default function SingleHotelPage() {
  // Getting the hotel id from the url
  const { hotelId } = useParams();

  // Using the context
  const { searchValues } = useContext(SearchContext);

  // State Handling
  const [hotelsData, setHotelsData] = useState([]);
  const [bookingsData, setBookingsData] = useState([]);
  const [pendingbookingsData, setPendingBookingsData] = useState([]);
  const [fetchedRooms, setFetchedRooms] = useState([]);
  const [allRequiredRooms, setAllRequiredRooms] = useState([]);
  const [otherRooms, setOtherRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dataAvailable, setDataAvailable] = useState(false);
  const [filtering, setFiltering] = useState(false);
  const [showRooms, setShowRooms] = useState(false);
  const [error, setError] = useState("");

  // Get Hotel's Data
  const singleHotel = async () => {
    const response = await fetch(
      `https://hotelbnb-backend.onrender.com/hotel/${hotelId}`
    );
    const result = await response.json();

    if (response.ok) {
      setHotelsData(result);
      setLoading(false);
    }
  };

  // --------- Reading/Fetching Bookings and Pending Bookings From Database ---------
  const readingBookings = async () => {
    if (!loading) {
      try {
        // Active Bookings
        const bookingResponse = await fetch(
          "https://hotelbnb-backend.onrender.com/read-bookings"
        );
        const bookingResult = await bookingResponse.json();

        if (!bookingResponse.ok) {
          setError("Bookings can't be fetched");
        } else if (bookingResponse.ok) {
          setTimeout(() => {
            setError("");
          }, 10000);
          setBookingsData(bookingResult);
        }

        // Pending Bookings
        const pendingBookingResponse = await fetch(
          "https://hotelbnb-backend.onrender.com/read-pending-bookings"
        );
        const pendingBookingResult = await pendingBookingResponse.json();

        if (!pendingBookingResponse.ok) {
          setError("Pending Bookings can't be fetched");
        } else if (pendingBookingResponse.ok) {
          setTimeout(() => {
            setError("");
          }, 10000);
          setPendingBookingsData(pendingBookingResult);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setDataAvailable(true);
      }
    }
  };

  // Reading/Fetching the specific hotel rooms data
  const readingRooms = async () => {
    if (dataAvailable) {
      try {
        const response = await fetch(
          "https://hotelbnb-backend.onrender.com/read-rooms"
        );
        const result = await response.json();
        if (!response.ok) {
          setError("Sorry, Something went wrong");
        } else if (response.ok) {
          setError("");

          // Filtering rooms that of this hotel
          const filteredRooms = Array.from(
            new Set(
              result.filter((room) => {
                return room.room_hotel_name == hotelsData.hotel_name;
              })
            )
          );

          // Available Rooms
          const availableRooms = [];

          // Looping rooms and bookings and checking one room against all pending and active bookings
          if (bookingsData.length > 0 || pendingbookingsData.length > 0) {
            for (let i = 0; i < filteredRooms.length; i++) {
              const selectedRoom = filteredRooms[i];
              let roomInBooking = false;
              const checkinDate = new Date(searchValues.checkin);
              const checkoutDate = new Date(searchValues.checkout);

              for (let j = 0; j < bookingsData.length; j++) {
                const selectedBooking = bookingsData[j];

                const bookingFrom = new Date(selectedBooking.booking_from);
                const bookingTo = new Date(selectedBooking.booking_to);
                if (
                  selectedRoom.room_number ===
                    selectedBooking.booking_room_number &&
                  ((checkinDate >= bookingFrom && checkinDate <= bookingTo) ||
                    (checkoutDate >= bookingFrom &&
                      checkoutDate <= bookingTo)) &&
                  selectedBooking.booking_status == "Active"
                ) {
                  roomInBooking = true;
                  break;
                }
              }

              if (pendingbookingsData.length > 0) {
                for (let k = 0; k < pendingbookingsData.length; k++) {
                  const selectedBooking = pendingbookingsData[k];

                  const bookingFrom = new Date(selectedBooking.booking_from);
                  const bookingTo = new Date(selectedBooking.booking_to);
                  if (
                    selectedRoom.room_number ===
                      selectedBooking.booking_room_number &&
                    ((checkinDate >= bookingFrom && checkinDate <= bookingTo) ||
                      (checkoutDate >= bookingFrom &&
                        checkoutDate <= bookingTo))
                  ) {
                    roomInBooking = true;
                    break;
                  }
                }
              }

              // Pushing the room to the available rooms if room is not in bookings
              if (!roomInBooking) {
                availableRooms.push(selectedRoom);
              }
            }
            setFetchedRooms(availableRooms);
          } else {
            setFetchedRooms(filteredRooms);
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        setFiltering(true);
      }
    }
  };

  /* Now available rooms are in fetchedRooms state variable,
  now we have to filter the rooms according to the
  number of guests and rooms selected */
  const filterResults = () => {
    if (filtering && fetchedRooms) {
      try {
        let rooms = searchValues?.counter?.rooms;
        const people =
          searchValues?.counter?.adults + searchValues?.counter?.children;
        const MaxPeopleInRoom = Math.ceil(people / rooms);

        // Looping on available rooms to check if the required rooms are available
        const requiredRooms = [];
        for (let i = 0; i < fetchedRooms.length; i++) {
          const room = fetchedRooms[i];
          if (room.room_max_people === MaxPeopleInRoom) {
            requiredRooms.push(room);
          }
        }

        if (
          (requiredRooms.length === 0 || requiredRooms.length < rooms) &&
          MaxPeopleInRoom > 1
        ) {
          const newMaxPeopleInRoom = MaxPeopleInRoom - 1;
          rooms += 1;

          for (let i = 0; i < fetchedRooms.length; i++) {
            const room = fetchedRooms[i];
            if (room.room_max_people === newMaxPeopleInRoom) {
              requiredRooms.push(room);
            }
          }
        }

        if (requiredRooms.length > 0) {
          setAllRequiredRooms(requiredRooms);
          const gettingOtherRooms = fetchedRooms.filter((room) => {
            return !requiredRooms.includes(room);
          });

          setOtherRooms(gettingOtherRooms);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setShowRooms(true);
      }
    }
  };

  // On first render hotel's data get fetched that changes the loading variable
  useEffect(() => {
    window.scrollTo(0, 0);
    singleHotel();
  }, []);

  // When loading variable gets changed it triggers the read bookings function that changes the available data variable
  useEffect(() => {
    readingBookings();
  }, [loading]);

  // And change of available data varaible triggers the rooms and filtering functions
  useEffect(() => {
    readingRooms();
    filterResults();
  }, [dataAvailable, fetchedRooms, filtering, searchValues]);

  if (loading)
    return (
      <div className="d-flex flex-column gap-2 justify-content-center align-items-center loading-state">
        <div className="spinner-grow" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );

  if (!loading)
    return (
      <>
        {/* Hero Section Start */}
        <div className="container-fluid p-0 SingleHotel-hero-section hero-section-spacing">
          <div className="row app-container">
            {/* Hotel Details Start */}
            <div className="col-12 hotel-page-details d-flex flex-md-row flex-column justify-content-between align-items-md-center align-items-start">
              {/* Side One Start - All Details */}
              <div className="side-one">
                <h3 className="text-capitalize">{hotelsData.hotel_name}</h3>
                <p className="mb-2 text-capitalize">
                  {hotelsData.hotel_location}, {hotelsData.hotel_city},{" "}
                  {hotelsData.hotel_country}
                </p>
                <p>{hotelsData.hotel_number}</p>
                <div className="d-flex rating align-items-center gap-2">
                  <p className="hotelRating d-flex justify-content-center align-items-center">
                    {hotelsData.hotel_rating}
                  </p>
                  <p className="hotelRatingReview text-capitalize">
                    {hotelsData.hotel_rating_review}
                  </p>
                </div>
              </div>
              {/* Side One End - All Details */}

              {/* Side Two Start - Price */}
              <div className="side-two">
                <p>
                  <span className="price">
                    ${hotelsData.hotel_per_day_price}{" "}
                  </span>
                  <span className="perDay">/day</span>
                </p>
              </div>
              {/* Side Two End - Price */}
            </div>
            {/* Hotel Details End */}

            {/* Hotel Image Start */}
            <div
              className="col-12 p-0 hotel-image"
              style={{ background: `url(${hotelsData.hotel_img})` }}
            ></div>
            {/* Hotel Image End */}
          </div>
        </div>
        {/* Hero Section End */}

        {/* Search The Best Room For You Section Start */}
        <div className="container-fluid p-0 search-best-room">
          <div className="row app-container">
            {/* Heading */}
            <h3 className="search-for-room-heading">
              Search The Best Room For You
            </h3>
            {/* Search Bar Start */}
            <HotelRoomSearchBar />
            {/* Search Bar End */}
          </div>
        </div>
        {/* Search The Best Room For You Section End */}

        {/* Room Cards Start */}
        <div className="container-fluid p-0 room-cards">
          <div className="row app-container room-cards-row">
            {/* Showing Alert based on http request response if hotels can't be fetched Start */}
            {error && <ErrorAlert errorMsg={error} />}
            {/* Showing Alert based on http request response if hotels can't be fetched End */}

            {!showRooms && <NothingToShow text="Getting the available rooms" />}
            {(!fetchedRooms.length) && showRooms ? <NothingToShow /> : ""}

            {allRequiredRooms.length
              ? allRequiredRooms?.map((singleRooms) => {
                  return (
                    <SingleUserSideRoomCard
                      key={singleRooms._id}
                      id={singleRooms._id}
                      img={singleRooms.room_imgOne}
                      roomName={singleRooms.room_name}
                      roomDesc={singleRooms.room_description}
                      roomBeds={singleRooms.room_beds}
                      roomArea={singleRooms.room_area}
                      roomMaxPeople={singleRooms.room_max_people}
                      roomPrice={singleRooms.room_per_day_price}
                    />
                  );
                })
              : ""}

            {otherRooms.length
              ? otherRooms?.map((singleRooms) => {
                  return (
                    <SingleUserSideRoomCard
                      key={singleRooms._id}
                      id={singleRooms._id}
                      img={singleRooms.room_imgOne}
                      roomName={singleRooms.room_name}
                      roomDesc={singleRooms.room_description}
                      roomBeds={singleRooms.room_beds}
                      roomArea={singleRooms.room_area}
                      roomMaxPeople={singleRooms.room_max_people}
                      roomPrice={singleRooms.room_per_day_price}
                    />
                  );
                })
              : ""}

            {!allRequiredRooms.length && !otherRooms.length
              ? fetchedRooms?.map((singleRooms) => {
                  return (
                    <SingleUserSideRoomCard
                      key={singleRooms._id}
                      id={singleRooms._id}
                      img={singleRooms.room_imgOne}
                      roomName={singleRooms.room_name}
                      roomDesc={singleRooms.room_description}
                      roomBeds={singleRooms.room_beds}
                      roomArea={singleRooms.room_area}
                      roomMaxPeople={singleRooms.room_max_people}
                      roomPrice={singleRooms.room_per_day_price}
                    />
                  );
                })
              : ""}
          </div>
        </div>
        {/* Room Cards End */}

        {/* Footer Start */}
        <Footer />
        {/* Footer End */}
      </>
    );
}
